import { apiBaseDisplay } from '@/services/Axios';
import { toLegacyDetail, v1Get, v1Put } from '@/services/v1Api';

/**
 * Wiki items are all assets from master_assets and every sub-table (cables, moving lights, etc.).
 * Each item has a composite id: "asset_type_subId" e.g. "moving_lights_5", "cables_10".
 */

/**
 * Numeric sub-row id from a wiki composite id (matches backend {@code parseWikiCompositeId}).
 * @param {string} compositeId e.g. "catalog_segment_12", "moving_lights_5"
 * @returns {number|null}
 */
export function parseWikiSubAssetId(compositeId) {
    if (compositeId == null || typeof compositeId !== 'string') {
        return null;
    }
    const idx = compositeId.lastIndexOf('_');
    if (idx <= 0) {
        return null;
    }
    const n = parseInt(compositeId.slice(idx + 1), 10);

    return Number.isFinite(n) ? n : null;
}

/**
 * Get flat list of wiki items. Classification filters and search are applied on the server.
 * @param {{ search?: string, department_id?: number, master_category_id?: number, category_id?: number }} params
 * @returns {Promise<{ success: boolean, data: Array }>}
 */
export const getWikiItems = async (params = {}) => {
  const { data } = await v1Get('/api/v1/wiki/items', params);
  return toLegacyDetail(Array.isArray(data) ? data : []);
};

/**
 * Get one wiki item by composite id (e.g. "moving_lights_5", "cables_10").
 * @param {string} id - composite id "asset_type_subId"
 * @returns {Promise<{ success: boolean, data: object }>} item with name, category, images, sections, spec (catalog columns), custom_overview_html
 */
export const getWikiItemById = async (id) => {
  const { data } = await v1Get(`/api/v1/wiki/items/${encodeURIComponent(id)}`);
  return toLegacyDetail(data);
};

/**
 * Update wiki catalog defaults for the linked master asset (system admin only).
 * @param {string} id - composite id e.g. "cables_10"
 * @param {{ department_id?: number|null, master_category_id?: number|null, category_id?: number|null, overview_html?: string|null }} payload
 */
export const updateWikiItem = async (id, payload = {}) => {
  const { data } = await v1Put(
    `/api/v1/wiki/items/${encodeURIComponent(id)}`,
    payload,
  );
  return toLegacyDetail(data);
};

/**
 * Resolve image URL for wiki/asset. If image_url is relative (e.g. storage path), prepend API base.
 * @param {string|null} imageUrl - from API (may be relative path or full URL)
 * @returns {string|null} URL suitable for <img src>
 */
export function resolveWikiImageUrl(imageUrl) {
  if (!imageUrl) return null;
  if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://') || imageUrl.startsWith('/')) {
    return imageUrl.startsWith('/') ? `${apiBaseDisplay.replace(/\/$/, '')}${imageUrl}` : imageUrl;
  }
  return `${apiBaseDisplay.replace(/\/$/, '')}/storage/${imageUrl}`;
}

// --- Guide API (keyed by composite wiki_item_id; requires auth) ---

/**
 * List all guides for a wiki item.
 * @param {string} wikiItemId - composite id e.g. "cables_10"
 * @returns {Promise<{ success: boolean, data: Array }>}
 */
export const getWikiGuides = async (wikiItemId) => {
  const { data } = await v1Get(`/api/v1/wiki/items/${encodeURIComponent(wikiItemId)}/guides`);
  return toLegacyDetail(Array.isArray(data) ? data : []);
};

/**
 * Get the prep guide for a wiki item.
 * @param {string} wikiItemId - composite id
 * @returns {Promise<{ success: boolean, data: object }|null>} guide or null if 404
 */
export const getPrepGuide = async (wikiItemId) => {
  try {
    const { data } = await v1Get(`/api/v1/wiki/items/${encodeURIComponent(wikiItemId)}/guides/prep`);
    return toLegacyDetail(data);
  } catch (e) {
    if (e.response?.status === 404) return null;
    throw e;
  }
};

/**
 * Get the deprep guide for a wiki item.
 * @param {string} wikiItemId - composite id
 * @returns {Promise<{ success: boolean, data: object }|null>} guide or null if 404
 */
export const getDePrepGuide = async (wikiItemId) => {
  try {
    const { data } = await v1Get(`/api/v1/wiki/items/${encodeURIComponent(wikiItemId)}/guides/deprep`);
    return toLegacyDetail(data);
  } catch (e) {
    if (e.response?.status === 404) return null;
    throw e;
  }
};

/**
 * Get a single guide by numeric id (for /wiki/guide/:id page).
 * @param {number|string} guideId - numeric guide id
 * @returns {Promise<{ success: boolean, data: object }>}
 */
export const getGuide = async (guideId) => {
  const { data } = await v1Get(`/api/v1/guides/${guideId}`);
  return toLegacyDetail(data);
};
